// --- Our Modules --------------------------------------------------------------------------------
import type { Translation } from "./types";
// --- Logic --------------------------------------------------------------------------------------
const en: Translation = {
    common: {
        loading: "Loading...",
        retry: "Try again",
        somethingWrong: "Something went wrong, please try again",
        logout: "Sign out",
        save: "Save",
        cancel: "Cancel",
        more: "More",
        close: "Close",
        confirm: "Confirm",
        done: "Done",
        copy: "Copy",
        copied: "Copied",
        share: "Share",
        typeToConfirm: "Type “{word}” to confirm",
        filters: "Filters",
        search: "Search",
        applyFilters: "Apply",
        clearFilters: "Clear",
        back: "Back",
        edit: "Edit",
        remove: "Delete",
        add: "Add",
        pagination: "Pagination",
        previousPage: "Previous page",
        nextPage: "Next page",
        pageCount: "{count} of {total} items",
        perPage: "per page",
    },
    bulk: {
        select: "Select",
        selected: "{count} selected",
        keep: "Unchanged",
        hint: "Only the fields you set are written to all of them",
        skipped: "{count} could not be changed",
        reviewTitle: "Review the change",
        reviewHint: "Written to every selected record ({count}), exactly as below. Nothing else on them is touched.",
        willChange: "What will change",
        fieldHint: "This is written to every selected record. Undo to leave the field as it is.",
        resultTitle: "Finished",
        changed: "{count} changed",
        removed: "{count} moved to the trash",
        restored: "{count} restored",
        purged: "{count} deleted for good",
        failed: "{count} unchanged",
        nothingChanged: "Nothing was changed",
        revertAll: "Undo all",
        andFailed: "Sales owns some of these records right now, or they are no longer there.",
        groupSet: "{count} set",
        groupCleared: "cleared",
        written: "will be written",
        pointPicked: "a point chosen",
        confirmRemove: "{count} selected. Move them to the trash?",
        confirmRestore: "{count} selected. Restore them?",
        confirmPurge: "{count} selected. Delete them for good? There is no way back.",
        restoreMany: "Restore",
        purgeMany: "Delete for ever",
        exit: "Cancel selection",
    },
    auth: {
        title: "Sign in",
        subtitle: "Enter your phone number and password to continue",
        phone: "Phone number",
        phonePlaceholder: "+9665XXXXXXXX",
        password: "Password",
        passwordPlaceholder: "••••••••",
        login: "Sign in",
        selectOrganization: "Choose an organization",
        selectOrganizationHint: "You can open more than one",
        noOrganizations: "No organization is linked to your account",
        phoneRequired: "Phone number is required",
        passwordRequired: "Password is required",
    },
    sideBar: {
        home: "Home",
        properties: "Properties",
        crm: "Customers",
        drive: "Files",
        team: "Team",
        settings: "Settings",
        subscription: "Subscription",
        support: "Help",
        trash: "Trash",
    },
    profile: {
        myProfile: "My account",
        subtitle: "Your own details, and what you reach in each company.",
        editProfile: "Edit details",
        memberSince: "Member since",
        fields: { name: "Name", phone: "Phone number", email: "Email", gender: "Gender", birthDate: "Date of birth" },
        genders: { male: "Male", female: "Female", unset: "Not set" },
        phoneHint: "Your number is your identity here, and only support can change it.",
        picture: "Picture",
        pictureHint: "Your colleagues see it in every company you work in.",
        changePicture: "Change picture",
        removePicture: "Remove picture",
        uploadFailed: "The picture could not be uploaded, please try again",
        notSet: "Not set",
        security: "Password",
        securityHint: "Change it whenever you like. A forgotten one needs support to issue a new one.",
        changePassword: "Change password",
        currentPassword: "Current password",
        newPassword: "New password",
        confirmPassword: "Confirm password",
        passwordHint: "Changing it signs you out everywhere else.",
        passwordTooShort: "The password must be at least {count} characters",
        passwordMismatch: "The two passwords do not match",
        temporaryPassword: "Your password is temporary — change it now",
        organizations: "Companies",
        organizationsHint: "The companies you work in, and the role you hold in each.",
        activeOrganization: "Current company",
        switchTo: "Switch",
        preferences: "Preferences",
        preferencesHint: "This browser only — they do not follow you to another device.",
        language: "Dashboard language",
    },
    subscription: {
        title: "Subscription",
        statuses: {
            pending: "Awaiting transfer",
            active: "Active",
            coming: "Queued",
            grace: "Grace period",
            ended: "Ended",
            cancelled: "Cancelled",
        },
        periods: { quarterly: "Quarterly", annually: "Annually" },
        periodEvery: { quarterly: "every 3 months", annually: "every year" },
        choosePlan: "Choose your plan",
        choosePlanHint: "One plan, two periods, and everything is unlimited except storage.",
        renewTitle: "Renew your subscription",
        renewHint: "The new period starts the day your current one ends, so you lose no time.",
        saveLabel: "Save {percent}%",
        storageIncluded: "{gb} GB of storage",
        gb: "GB",
        extraStorage: "Extra storage",
        extraStorageHint: "{gb} GB for {price} {currency}, added to the plan and carried into the next one.",
        packLabel: "pack",
        totalStorage: "Total storage",
        total: "Total",
        request: "Request subscription",
        reviewTitle: "Review your request",
        reviewHint: "Once confirmed, transfer the amount and send us the receipt — it goes live as soon as we confirm it.",
        planLine: "{period} plan",
        addonsLine: "{count} × extra storage",
        daysLeft: "days left",
        startDate: "Started",
        endDate: "Ends",
        renew: "Renew",
        renewNow: "Renew now",
        reactivate: "Reactivate",
        graceTitle: "Your period has ended",
        graceNote: "Everything still works during the grace window, then the account goes read-only.",
        graceEnds: "Grace ends",
        endedTitle: "Your account is read-only",
        endedNote: "Nothing has been deleted. Renew and everything works exactly as it did.",
        freeTitle: "You are on the free quota",
        freeNote: "Enough to try it with. A subscription opens everything but storage.",
        pendingTitle: "Your request is waiting for its transfer",
        pendingNote: "Transfer the amount and send us the receipt — it goes live as soon as we confirm it.",
        pendingRenewalTitle: "A renewal is waiting for its transfer",
        pendingRenewalNote: "Your current period runs as normal, and the new one is queued behind it once the transfer is confirmed.",
        amountLabel: "Amount due",
        payBy: "Transfer by",
        requestedBy: "Requested by",
        howToPay: "How to pay",
        bankLabel: "Bank",
        accountName: "Account name",
        iban: "IBAN",
        sendReceipt: "Send the receipt on WhatsApp",
        usageTab: "Usage",
        currentPeriod: "Current period",
        nextPeriod: "Next period",
        used: "Used",
        available: "Available",
        buyStorage: "Buy storage",
        buyStorageTitle: "Extra storage",
        buyStorageHint: "Added to your running period as soon as the transfer is confirmed, and carried into the next one.",
        packs: "Packs",
        storagePendingTitle: "A storage request is waiting for its transfer",
        storagePendingNote: "The space is not added to your plan until the transfer is confirmed.",
        withdrawStorage: "Withdraw the storage request",
        withdrawStorageConfirm: "The storage request will be cancelled and you can ask for a different amount afterwards.",
        addedStorage: "Extra storage",
        withdraw: "Withdraw the request",
        withdrawConfirm: "The request will be cancelled and you can ask for a different plan afterwards.",
        comingTitle: "Queued period",
        comingNote: "Already paid for, and it starts automatically when your current period ends.",
        comingStarts: "Starts",
        usageTitle: "Usage",
        usageHint: "What you have used of your current limits.",
        projects: "Projects",
        units: "Units",
        customers: "Customers",
        team: "Team",
        storage: "Storage",
        unlimited: "Unlimited",
        ofLimit: "of {limit}",
        needMoreStorage: "Need more space?",
        needMoreStorageNote: "Extra storage is added to your running period right away and carried into the next.",
        contactUs: "Contact us",
        historyTitle: "History",
        historyEmpty: "No past periods.",
        paidOn: "Paid",
        cancelledOn: "Cancelled",
        requestedOn: "Requested",
    },
    settings: {
        catalogTitle: "Your public catalog",
        catalogHint: "Everything on this screen is what visitors see on your catalog.",
        identity: "Identity",
        brand: "Brand",
        contacts: "Contact details",
        contactsHint: "Shown on the catalog so a buyer can reach you without filling in a form.",
        languagesHint: "The languages your catalog is published in. Visitors are offered these only.",
        aboutHint: "A short paragraph about the company. It opens the company sheet on your catalog.",
        coverHint: "Sits behind the logo on the company sheet. Without one, the brand colour is used.",
        colorHint: "Used for buttons and highlights across the catalog.",
        logoHint: "Pick one of your images, or upload a new one.",
        noLogo: "No logo",
        saved: "Saved",
        nameRequired: "The organization name is required",
        languagesRequired: "Choose at least one language",
        colorInvalid: "The colour must be a hex value such as #1F6F5C",
        fields: {
            name: "Organization name",
            languages: "Published languages",
            color: "Brand colour",
            logo: "Logo",
            about: "About the company",
            cover: "Cover picture",
            phone: "Phone",
            whatsapp: "WhatsApp",
            email: "Email",
            city: "City",
            address: "Address",
            crNumber: "Commercial registration",
        },
    },
    team: {
        add: "Add",
        addTitle: "Add a member",
        addHint: "If the number is already registered, they join with their existing account and password.",
        name: "Name",
        namePlaceholder: "Full name",
        nameRequired: "The name is required",
        phoneRequired: "A phone number is required",
        role: "Role",
        empty: "No members yet",
        emptyHint: "Add whoever works in this company. A phone number already registered joins with its own account.",
        you: "(you)",
        temporaryPassword: "Temporary password",
        passwordTitle: "Password",
        passwordHint: "Share this with {name}. It is shown once — after that a new one can be issued, as long as they have not changed it.",
        confirmRole: "Turn {name} into a {role}?",
        confirmRemove: "Remove {name} from your organization? Their account stays; only their access here goes.",
        confirmTransfer: "Transfer ownership to {name}? You become a manager, and you cannot undo it yourself.",
        confirmPassword: "Issue a new password for {name}? Every session they have open will end.",
        transferWord: "transfer",
        actions: { turnTo: "Turn into {role}", reissuePassword: "Issue a password", transferOwnership: "Transfer ownership", remove: "Remove" },
    },
    drive: {
        upload: "Upload",
        uploadTitle: "Upload files",
        uploadHint: "Images, videos, documents and 3D files. Everything you upload counts against your storage.",
        pick: "Choose files",
        pickHint: "or drop them here",
        selected: "{count} selected",
        clear: "Clear",
        root: "My files",
        allFiles: "All files",
        search: "Search files",
        empty: "No files yet",
        gallery: "Gallery",
        slots: {
            picture: "Portrait",
            gallery: "Gallery",
            logo: "Logo",
            paper: "Signed contract",
            receipt: "Receipts",
            identity: "ID copy",
        },
        recordFiles: "The record's own files",
        emptyHint: "Upload from a project, a unit or a contract and the file is filed under it automatically.",
        emptyRecord: "No files on this record yet.",
        emptyRecordHint: "Pictures, drawings and signed papers filed here stay with the record.",
        generalFiles: "General files",
        emptySlot: "Nothing here yet.",
        pickFromDrive: "Pick from the drive",
        emptyGallery: "No photos yet",
        otherFiles: "Files",
        emptyFolder: "This folder is empty",
        used: "Used",
        ofAllowed: "of {allowed}",
        freeQuota: "Free quota",
        full: "Storage is full",
        files: "files",
        name: "Name",
        namePlaceholder: "A name you will recognise",
        tags: "Tags",
        tagsPlaceholder: "Separate with commas",
        renameTitle: "Rename file",
        confirmRemove: "Delete {name}? This cannot be undone and the file is gone immediately.",
        unnamedRecord: "Record",
        noPreview: "No preview for this file type",
        loose: "Loose files",
        categories: { image: "Images", video: "Videos", document: "Documents", model: "3D files", archive: "Archives" },
        owners: {
            Organization: "Company",
            Project: "Projects",
            Unit: "Units",
            Customer: "Customers",
            Contract: "Contracts",
            Reservation: "Reservations",
            Installment: "Installments",
            Warranty: "Warranties",
            others: "Others",
        },
        actions: { download: "Download", rename: "Rename", replace: "Replace", remove: "Delete" },
    },
    uploads: {
        uploading: "Uploading {done} of {total}",
        allDone: "Uploads finished",
        someFailed: "{count} failed",
        waiting: "Waiting",
        cancelled: "Cancelled",
        retry: "Try again",
        clear: "Clear",
        collapse: "Collapse",
        expand: "Expand",
    },
    crm: {
        title: "Customers",
        stats: {
            new: "New interests",
            offer: "Offers",
            won: "Won",
        },
        actions: {
            contact: "Contact",
            profile: "Profile",
            open: "Open interest",
        },
        picture: "Picture",
        pictureHint: "Optional",
        removePicture: "No picture",
        confirmBlock: "Block {name}? Their catalog enquiries will be accepted and thrown away, and they are never told.",
        confirmUnblock: "Unblock {name}? Their enquiries will be accepted again.",
        noOpenLead: "No open interest",
        ownsUnit: "Owns",
        leads: "Interests",
        customers: "Customers",
        addLead: "New interest",
        addCustomer: "New customer",
        editCustomer: "Edit customer",
        editLead: "Edit interest",
        searchLeads: "Search by name, phone or email",
        searchCustomers: "Search by name, phone or email",
        emptyLeads: "No interests yet. Add the first one, or wait for the catalog to bring one in.",
        leadNotFound: "This interest is not here.",
        leadNotFoundHint: "It may have been deleted, in which case the trash still has it.",
        customerNotFound: "This person is not here.",
        customerNotFoundHint: "They may have been deleted, in which case the trash still has them.",
        emptyCustomers: "Nobody here yet.",
        emptyInteractions: "Nothing logged yet.",
        emptyInteractionsHint: "Every call, message and visit — and the ones booked for later.",
        emptyNotes: "No notes yet.",
        emptyReservations: "This interest has not reached a reservation.",
        reservations: "Reservations",
        general: "General interest",
        unnamed: "Unnamed",
        notSet: "—",
        never: "Never",
        logInteraction: "Log a call",
        bookFollowUp: "Book a follow-up",
        editInteraction: "Edit entry",
        closeLead: "Close",
        reopenLead: "Reopen",
        moveStage: "Move stage",
        addNote: "Add a note",
        writeNote: "Write a note",
        block: "Block",
        unblock: "Unblock",
        blocked: "Blocked",
        notBlocked: "Not blocked",
        blockedHint: "Their catalog enquiries are accepted and thrown away. They are never told.",
        closedBadge: "Closed",
        showClosed: "Show closed",
        unassigned: "Unassigned",
        lastContact: "Last contact",
        interestedIn: "Interested in",
        budget: "Budget",
        wonHint: "A lead becomes won when a reservation or a contract is made.",
        backwardsHint: "You can only move a lead forward. If it died, mark it lost with a reason.",
        closeHint: "Only an untouched lead can be closed, and closing is not losing — it clears duplicates off the board.",
        lostHint: "Say why, or the funnel cannot tell a real loss from a tidied one.",
        followUpHint: "Pick a date in the future and this becomes a follow-up you owe.",
        mergedHint: "They already had an open enquiry, so this was added to it.",
        conflictTitle: "You have another customer with this phone number",
        conflictHint: "One person is one record. Open the one that already holds this number, or bring this one back under a different one.",
        viewCustomer: "View that customer",
        changePhone: "Change this one's phone",
        newPhone: "New phone number",
        interests: "Interests",
        addInterest: "Record what they are after",
        editInterest: "Edit what they are after",
        emptyInterests: "Nothing recorded yet.",
        emptyInterestsHint: "Write down what they are looking for, so whoever picks this up knows.",
        interestsTitle: "What they are after",
        confirmRemoveInterest: "Delete this line?",
        confirmRemoveNote: "Delete this note?",
        confirmStage: "Move this interest to “{stage}”?",
        responsible: "Responsible",
        activity: "Activity",
        memberSince: "Customer since",
        openNote: "Open note",
        editNote: "Edit note",
        noteText: "Note",
        holderSold: "Sold to",
        holderRented: "Rented to",
        holderReserved: "Reserved for",
        holderUntil: "Until",
        holderDeposit: "Deposit",
        emptyPersonLeads: "This person has no interests yet.",
        emptyPersonLeadsHint: "Open one for whatever they are asking about.",
        emptyProjectLeads: "No interests on this project yet.",
        emptyProjectLeadsHint: "Add one for anybody who has asked about this development.",
        emptyUnitLeads: "No interests on this unit yet.",
        emptyUnitLeadsHint: "An enquiry filed against this unit appears here, from the catalog or from your team.",
        recordedBy: "Recorded by",
        addInteraction: "Log a contact",
        pastOrPlanned: "Add a past contact",
        pastOrPlannedOff: "Schedule a new contact",
        plannedHint: "This lands in what you owe. Pick a date in the future.",
        pastHint: "Write down what actually happened. A future date is not allowed here.",
        schedule: "Booked follow-ups",
        voiceStart: "Dictate the summary",
        voiceStop: "Stop dictating",
        voiceUnsupported: "Voice dictation is not supported in this browser",
        payMethod: "Payment",
        rangeFrom: "From",
        rangeTo: "To",
        all: "All",
        today: "Today",
        logWhatHappened: "Record what happened",
        logWhatHappenedHint: "It was due — write the outcome down.",
        interactionDetails: "The contact",
        answered: "They answered",
        answeredHint: "Nothing to record until it has happened",
        subjectLead: "Interest request",
        noSummary: "Nothing was written down.",
        allInterests: "All interests",
        mineInterests: "Mine",
        assignedToMe: "Assigned to me",
        allContacts: "All contacts",
        mineContacts: "Mine",
        frozenPast: "It already happened, so only the outcome, the summary, the notes and the channel can change.",
        tabs: {
            timeline: "Contact",
            details: "Details",
            notes: "Notes",
            leads: "Interests",
            files: "Files",
        },
        due: {
            title: "Follow-ups",
            overdue: "Overdue",
            upcoming: "Upcoming",
            open: "Open",
            all: "All",
        },
        fields: {
            name: "Name",
            phone: "Phone",
            email: "Email",
            nationalId: "National ID",
            status: "Status",
            kind: "Property type",
            project: "Project",
            unit: "Unit",
            source: "Came from",
            stage: "Stage",
            financing: "Paying by",
            budgetMin: "Budget from",
            budgetMax: "Budget to",
            assignees: "Held by",
            lostReason: "Why it was lost",
            closeReason: "Why close it",
            channel: "How",
            category: "Kind",
            direction: "Direction",
            outcome: "Outcome",
            date: "When",
            summary: "Summary",
            notes: "Notes",
            firstContact: "First contact",
        },
        stages: {
            new: "New",
            contacted: "Contacted",
            qualified: "Qualified",
            offer: "Offer",
            won: "Won",
            lost: "Lost",
        },
        statuses: {
            lead: "Lead",
            client: "Client",
        },
        sources: {
            catalog: "Catalog",
            manual: "Entered by hand",
            call: "Phone call",
            whatsapp: "WhatsApp",
            walkIn: "Walk-in",
            referral: "Referral",
            exhibition: "Exhibition",
            snapchat: "Snapchat",
            instagram: "Instagram",
            tiktok: "TikTok",
            facebook: "Facebook",
            x: "X",
            google: "Google",
            portal: "Property portal",
            other: "Other",
        },
        lostReasons: {
            price: "Price",
            timing: "Timing",
            boughtElsewhere: "Bought elsewhere",
            unreachable: "Could not reach them",
            notSerious: "Not serious",
            other: "Other",
        },
        closeReasons: {
            duplicate: "Duplicate",
            superseded: "Replaced by another",
            mistake: "Entered by mistake",
            other: "Other",
        },
        interestStatuses: {
            active: "Keen",
            hesitant: "Hesitant",
            stopped: "Stopped",
        },
        financing: {
            cash: "Cash",
            mortgage: "Mortgage",
        },
        channels: {
            call: "Call",
            whatsapp: "WhatsApp",
            sms: "SMS",
            email: "Email",
            visit: "Visit",
            catalog: "Catalog",
            other: "Other",
        },
        categories: {
            marketing: "Marketing",
            response: "Response",
            followUp: "Follow-up",
            guarantee: "Warranty",
            other: "Other",
        },
        directions: {
            inbound: "Received",
            outbound: "Outgoing",
        },
        outcomes: {
            completed: "Done",
            failed: "No answer",
            pending: "Not yet",
        },
        counting: {
            leads: "Requests",
            open: "open",
            interactions: "Entries",
        },
        confirmRemoveLead: "Move {name}'s interest request to the trash?",
        confirmRemoveCustomer: "Move {name} to the trash?",
        confirmRemoveInteraction: "Move this contact to the trash?",
    },
    sales: {
        title: "Sales",
        reserve: "Reserve",
        sell: "Sell",
        rent: "Rent",
        closeDeal: "Close the deal",
        byReservation: "Reserve the unit",
        bySale: "Sale or rent contract",
        contracts: "Contracts",
        reservations: "Reservations",
        installments: "Installments",
        plans: "Payment plans",
        emptyContracts: "Nothing signed or held here yet.",
        emptyContractsHint: "Contracts, reservations and payment plans for this record appear together here.",
        emptyReservations: "No reservations here yet.",
        emptyReservationsHint: "A hold keeps the unit in this person's name until it becomes a contract or is cancelled.",
        emptyInstallments: "Nothing is owed right now.",
        emptyInstallmentsHint: "Installments are created with a payment plan when a contract is filed.",
        pickUnit: "Pick the unit",
        pickCustomer: "Who took it",
        pickFromInterests: "From their interests",
        pickAnyUnit: "Any unit",
        deposit: "Deposit",
        expiresAt: "Hold expires",
        signedAt: "Signed on",
        startsAt: "Starts",
        endsAt: "Ends",
        amount: "Amount",
        contractDetails: "Contract details",
        reservationDetails: "Hold details",
        listPrice: "Listed price",
        discount: "Discount",
        priorHold: "The hold before it",
        recordedBy: "Recorded by",
        recordedAt: "Recorded on",
        papers: "Papers and attachments",
        noPaper: "The signed contract has not been uploaded yet.",
        noFiles: "Nothing is attached to this record.",
        otherFile: "Another file",
        addFile: "Add a file",
        paperHint: "Upload the signed copy now, or later from the contract itself.",
        addPlan: "Payment plan",
        planCount: "Installments",
        planPeriod: "Every N months",
        planStart: "First one due",
        dueAt: "Due",
        paid: "Paid",
        remaining: "Remaining",
        overdue: "Overdue",
        upcoming: "Upcoming",
        addPayment: "Record a payment",
        viewSchedule: "The schedule",
        paymentAmount: "Amount paid",
        paidAt: "Paid on",
        cancelReservation: "Cancel the hold",
        cancelContract: "Cancel the contract",
        confirmCancelReservation: "Cancel this hold and put the unit back on the shelf?",
        confirmCancelContract: "Cancel this contract and put the unit back on the shelf?",
        installmentOf: "Installment {number} of {count}",
        statuses: { pending: "Due", paid: "Paid", cancelled: "Cancelled" },
        planStatuses: { active: "Running", completed: "Completed", cancelled: "Cancelled" },
        emptyPlans: "No payment plans yet.",
        emptyPlansHint: "A plan is written when a contract is paid over time.",
        contractStatuses: { live: "Active", cancelled: "Cancelled" },
        reservationStatuses: { active: "Active", expired: "Expired", done: "Became a contract", cancelled: "Cancelled" },
        cancelReasons: {
            expired: "Lapsed",
            customerWithdrew: "Customer withdrew",
            mistake: "Entered by mistake",
            terminated: "Terminated",
            other: "Other",
        },
    },
    warranties: {
        title: "Warranties",
        tab: "Warranties",
        one: "Warranty",
        state: { notStarted: "Not started", active: "Covered", expiring: "Running out", expired: "Expired", unknown: "No end date" },
        stateHint: {
            notStarted: "The clock starts when the unit is sold.",
            active: "Covered",
            expiring: "Runs out within {days} days",
            expired: "The cover has ended",
            unknown: "Nobody said when it ends",
        },
        provider: "Guarantor",
        providerPhone: "Guarantor's phone",
        months: "Cover length",
        monthsRequired: "How many months the cover lasts is required",
        startsAt: "Starts",
        endsAt: "Ends",
        daysLeft: "{days} days left",
        remaining: "Left",
        covers: "Covers",
        notStartedHint: "What this unit comes with. The sale turns each one into a tracked warranty.",
        contactAbout: "Log a contact",
        edit: "Edit the warranty",
        empty: "No warranties on this unit.",
        emptyHint: "Record what is covered, for how long, and by whom — the sale starts the clock.",
        emptyTemplates: "Nothing is covered yet.",
        emptyTemplatesHint: "Add what this unit comes with before it is sold.",
        emptyCustomer: "This person holds no warranties.",
        emptyCustomerHint: "They appear here once a unit they bought carries one.",
        lockedBySale: "This unit is sold, so its warranties are live records now and cannot be edited here.",
        allUnits: "Every unit",
        allStates: "Any state",
        allSubjects: "Everything",
        aboutWarranty: "About a warranty",
        beforeSale: "Before the sale",
        subject: "What it is about",
        details: "The warranty",
        timeline: "The cover",
        duration: "Duration",
        daysUnit: "days",
        elapsed: "Elapsed",
        unit: "Unit",
        customer: "Customer",
        callProvider: "Call the guarantor",
        noProvider: "No guarantor recorded",
        holder: "Held by",
        under: "On",
    },
    properties: {
        title: "Projects & Units",
        projects: "Projects",
        units: "Units",
        addProject: "New project",
        addUnit: "New unit",
        addUnits: "Several units",
        editProject: "Edit project",
        editUnit: "Edit unit",
        duplicateUnit: "Duplicate",
        searchProjects: "Search by name, city or plan number",
        searchUnits: "Search by unit or deed number",
        emptyProjects: "No projects yet. Create the first one to start adding units.",
        emptyUnits: "No units here yet.",
        emptyUnitsHint: "Add them one at a time, or a whole floor at once.",
        emptyFiltered: "Nothing matches the filter you set. Clear it to see the rest.",
        emptyBuildings: "No buildings yet.",
        emptyBuildingsHint: "Add one if this project is divided into blocks or towers.",
        emptyWarranties: "No warranties on this unit.",
        emptyWarrantiesHint: "Record what is covered, for how long, and by whom.",
        emptyFeatures: "No features written yet.",
        shareHint: "This is the public page. It works for anybody, with no sign-in.",
        openCatalog: "Open the page",
        unnamed: "Unnamed",
        emptyDetails: "Nothing recorded yet.",
        emptyDetailsHint: "Whatever is filled in on the edit panel shows up here.",
        emptyBuildingsShort: "No buildings",
        countsHint: "Leave a box empty for anything nobody has said yet.",
        flagsHint: "Yes, no, or the dash for anything nobody has said yet.",
        tabs: {
            units: "Units",
            buildings: "Buildings",
            files: "Files",
            details: "Details",
            warranties: "Warranties",
        },
        fields: {
            name: "Name",
            nameAr: "Name (Arabic)",
            nameEn: "Name (English)",
            slug: "Custom link",
            slugHint: "Optional",
            description: "Description",
            types: "Property types",
            stage: "Stage",
            city: "City",
            address: "Address",
            location: "Map location",
            planNumber: "Plan number",
            plotNumber: "Plot number",
            wafiNumber: "Wafi licence",
            startedAt: "Started on",
            deliveryDate: "Delivery date",
            notes: "Notes",
            isPublic: "In the catalog",
            /** The question a form asks. `isPublic` stays the short noun a chip and a fact label need. */
            showInCatalog: "Show in the catalog",
            project: "Project",
            building: "Building",
            number: "Unit number",
            floor: "On floor",
            type: "Type",
            usage: "Usage",
            offer: "Offered for",
            status: "Status",
            price: "Price",
            rentPeriod: "Rent period",
            rentPeriodHint: "How many months one price covers",
            deedNumber: "Deed number",
            useProjectGallery: "Use the project's gallery",
            provider: "Provider",
            providerPhone: "Provider phone",
            months: "Months",
            numbers: "Unit numbers",
            numbersHint: "Type a number and add it. A range like 301-312, or a pasted comma-separated row, both work.",
        },
        groups: { basics: "Basics", measures: "Areas", counts: "Rooms & counts", flags: "Features", choices: "Specifications" },
        measures: { landArea: "Land area", builtArea: "Built area", streetWidth: "Street width" },
        counts: {
            bedrooms: "Bedrooms",
            masterBedrooms: "Master bedrooms",
            livingRooms: "Living rooms",
            receptions: "Receptions",
            bathrooms: "Bathrooms",
            kitchens: "Kitchens",
            maidRooms: "Maid rooms",
            driverRooms: "Driver rooms",
            storageRooms: "Storage rooms",
            parkings: "Parkings",
            balconies: "Balconies",
            pools: "Pools",
            elevators: "Elevators",
            floors: "Floors inside",
        },
        flags: {
            hasBasement: "Basement",
            hasAnnex: "Annex",
            hasRoof: "Roof",
            hasGarden: "Garden",
            hasPrivateEntrance: "Private entrance",
            hasDriveway: "Driveway",
            hasLaundryRoom: "Laundry room",
            hasInternalStairs: "Internal stairs",
            hasFittedKitchen: "Fitted kitchen",
            isFurnished: "Furnished",
            hasFiberOptic: "Fiber optic",
            hasWater: "Water",
            hasElectricity: "Electricity",
            hasSewage: "Sewage",
            isNew: "Brand new",
        },
        choices: { acSystem: "Air conditioning", facades: "Facades" },
        unitTypes: {
            apartment: "Apartment",
            villa: "Villa",
            duplex: "Duplex",
            townhouse: "Townhouse",
            floor: "Floor",
            studio: "Studio",
            office: "Office",
            shop: "Shop",
            warehouse: "Warehouse",
            garage: "Garage",
            land: "Land",
            other: "Other",
        },
        stages: { planning: "Planning", underConstruction: "Under construction", ready: "Ready" },
        usages: { residential: "Residential", commercial: "Commercial" },
        offers: { sale: "Sale", rent: "Rent" },
        statuses: { available: "Available", reserved: "Reserved", sold: "Sold", rented: "Rented" },
        acSystems: { central: "Central", split: "Split", window: "Window", concealed: "Concealed", other: "Other", none: "None" },
        facades: { north: "North", south: "South", east: "East", west: "West" },
        warrantyTypes: {
            structure: "Structure",
            waterproofing: "Waterproofing",
            plumbing: "Plumbing",
            electrical: "Electrical",
            hvac: "HVAC",
            elevators: "Elevators",
            finishes: "Finishes",
            appliances: "Appliances",
            other: "Other",
        },
        counting: { units: "units", available: "available", buildings: "buildings" },
        perMonth: "every {months} months",
        months: "months",
        sqm: "m²",
        riyal: "SAR",
        currency: "SAR",
        rentUnits: { month: "month", quarter: "quarter", half: "half-year", year: "year", months: "months" },
        yes: "Yes",
        no: "No",
        unknown: "Not said",
        notSet: "—",
        statusLocked: "Sales owns this unit's status right now",
        moveStatus: "Move the unit",
        confirmStatus: "Put this unit back as “{status}”?",
        putBackHint: "Nothing is holding it, so this is a correction rather than a cancellation.",
        mapPlaceholder: "The map arrives later. The saved point is kept.",
        confirmRemoveProject: "Delete “{name}” and all its units?",
        confirmRemoveUnit: "Delete “{name}”?",
        confirmRemoveBuilding: "Remove building “{name}”?",
        confirmRemoveWarranty: "Remove this warranty?",
        addBuilding: "Add building",
        renameBuilding: "Rename building",
        addWarranty: "Add warranty",
        editWarranty: "Edit warranty",
        bulkTitle: "Create several units",
        bulkHint: "Every unit is created with whatever you fill in here. Fill in what differs afterwards.",
        bulkPreview: "{count} units will be created",
        nameRequired: "The name is required",
        numberRequired: "The unit number is required",
        projectRequired: "Choose a project",
        numbersRequired: "Add at least one unit number",
        incomplete: "Files still uploading",
    },
    languages: { ar: "Arabic", en: "English" },
    trash: {
        title: "Trash",
        empty: "Nothing has been deleted.",
        emptyHint: "Deleted records wait here for {days} days, then they are cleared for good.",
        emptyNow: "Empty the trash",
        emptyWord: "DELETE",
        confirmEmpty: "Everything here is deleted permanently, with its files. This cannot be undone.",
        confirmPurge: "“{name}” is deleted permanently, with its files. This cannot be undone.",
        restore: "Restore",
        purge: "Delete permanently",
        deletedAt: "Deleted",
        clearsIn: "cleared in {days} days",
        clearsToday: "cleared today",
        retention: "Kept for {days} days",
        models: {
            Project: "Projects",
            Unit: "Units",
            Customer: "Customers",
            Lead: "Interests",
            Interaction: "Contacts",
            Contract: "Contracts",
            Reservation: "Reservations",
            InstallmentPlan: "Payment plans",
            Installment: "Installments",
            Warranty: "Warranties",
        },
    },
    history: { title: "History", empty: "Nothing has happened to this record yet.", system: "System", open: "History" },
    roles: { owner: "Owner", manager: "Manager", member: "Member" },
    home: {
        title: "Home",
        greetingMorning: "Good morning",
        greetingAfternoon: "Good afternoon",
        greetingEvening: "Good evening",
        catalogLabel: "Public catalog",
        profileLabel: "My account",
        workTitle: "What's new?",
        workClear: "Nothing waiting — you are up to date.",
        work: {
            followUpsOverdue: "Follow-ups already late",
            followUpsToday: "Follow-ups due today",
            installmentsOverdue: "Installments overdue",
            reservationsExpiring: "Reservations expiring",
            contractsUnsigned: "Contracts not signed",
            leadsStale: "Interests nobody has contacted",
        },
        projects: "Projects",
        units: "Units",
        customers: "Customers",
        leads: "Open interests",
        shelfTitle: "Units",
        pipelineTitle: "Interests",
        trendTitle: "The last six months",
        trendLeads: "New interests",
        trendDeals: "Deals signed",
        collectionsTitle: "Installments",
        collected: "Collected",
        upcoming: "Upcoming",
        overdue: "Overdue",
        storageTitle: "Storage",
        storageUsed: "Used",
        storageFree: "Free",
        storageOf: "{used} of {allowed}",
        teamTitle: "The team",
        subscriptionTitle: "Subscription",
        subscriptionDaysLeft: "{days} days left",
        subscriptionLastDay: "Ends today",
        subscriptionEndsAt: "Ends {date}",
        subscriptionNone: "No active subscription",
        subscriptionPending: "A payment request is waiting to be confirmed.",
        subscriptionQueued: "A renewal is queued and starts when this period ends.",
        subscriptionGrace: "Your period ended — full access until {date}.",
        manage: "Manage",
        nothingYet: "Nothing yet",
        freeQuota: "You are on the free quota",
        readOnly: "Your subscription ended — this account is read-only",
    },
    support: {
        title: "Help",
        ask: "Ask for help",
        askTitle: "Ask for help",
        askHint: "Tell us what you need and we will come back to you. Keep it to one thing per request.",
        hint: "Requests you send us appear here with our answer.",
        send: "Send",
        statuses: { pending: "Waiting on us", contacted: "We are on it", solved: "Solved" },
        fields: { subject: "Subject", message: "What do you need?" },
        replyTitle: "Our answer",
        detailsTitle: "Request details",
        answered: "Answered",
        noReply: "We have not answered yet. The answer appears here — there is no notification to wait for, so open the request to read it.",
        hide: "Remove",
        confirmHide: "Remove this request from your list? We keep our copy, so the answer may still reach you.",
        capped: "You have {max} open requests. We will answer those before you can send another.",
        empty: "You have not asked us for anything yet.",
        emptyHint: "Billing, a screen behaving oddly, something you cannot find — send it and we will look.",
    },
    screens: {
        properties: "Projects & Units",
        crm: "Customers",
        drive: "File storage",
        team: "Team",
        settings: "Settings",
        subscription: "Subscription",
        support: "Help",
        profile: "My account",
        notFound: "Page not found",
    },
};

export default en;
